// What operators write over the Amazon overview's Steps 2 and 3 with ?edit=1, one value per
// building and key. Mirrors src/inrange_agents/amazon_building_notes.py; the keys are built in
// routes/amazonOverview/overrides.ts.
import { useMutation, useQuery, useQueryClient } from "react-query";

import type { PostJsonError } from "../http/postJson";
import { QK } from "../queryKeys";
import { useAgentsHttp } from "../useAgentsHttp";

export interface BuildingNote {
  project_id: string;
  /** The key: a bare step id for its card line, else a field such as "gridoffer.due". */
  step_id: string;
  text: string;
  /** What the page showed for the key when this was written; null on older card lines. */
  platform: string | null;
  version: number;
  updated_at: string;
  updated_by: string | null;
}

export interface BuildingNotePut {
  project_id: string;
  step_id: string;
  /** The note's version as read; 0 for a line never written. */
  version: number;
  /** null clears the note, putting the platform's value back. */
  text: string | null;
  platform: string;
}

export const NOTE_MOVED =
  "Not saved: someone else changed this value a moment ago. The page now shows their change; check it and try again.";

export const useAmazonBuildingNotes = () => {
  const http = useAgentsHttp();
  return useQuery<BuildingNote[]>(
    [QK.amazonBuildingNotes],
    async () =>
      (await http<{ notes: BuildingNote[] }>("/amazon-overview/notes/list", {}))
        .notes,
    { refetchOnWindowFocus: true },
  );
};

export const usePutBuildingNote = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<{ note: BuildingNote | null }, Error, BuildingNotePut>(
    (body) =>
      http<{ note: BuildingNote | null }>("/amazon-overview/notes/put", body).catch(
        (err: PostJsonError) => {
          throw err.status === 409 ? new Error(NOTE_MOVED) : err;
        },
      ),
    {
      // The saved value shows at once, so a form opened before the reload lands reads it.
      onSuccess: ({ note }, body) => {
        qc.setQueryData<BuildingNote[]>([QK.amazonBuildingNotes], (old) => [
          ...(old ?? []).filter(
            (n) => n.project_id !== body.project_id || n.step_id !== body.step_id,
          ),
          ...(note ? [note] : []),
        ]);
      },
      // Saved or refused, reload: a refusal means someone else's change is waiting. The
      // buildings reload too, for the bank holidays of any year a new date reaches.
      onSettled: () => {
        void qc.invalidateQueries([QK.amazonBuildingNotes]);
        void qc.invalidateQueries([QK.amazonOverview]);
      },
    },
  );
};
